/* DDT Made Theme — ddtmade.js */

(function () {
  'use strict';

  // Translated strings, output by layout/theme.liquid.
  const theme = window.theme || {};
  const t = (key, fallback) => (theme.strings && theme.strings[key]) || fallback;

  // ─── Theme Toggle ───────────────────────────────────────────
  // The saved theme is applied before first paint by an inline script in
  // theme.liquid. The button renders both states; CSS shows the matching one.
  const THEME_KEY = 'ddt-theme';

  function initThemeToggle() {
    document.getElementById('theme-toggle')?.addEventListener('click', () => {
      const root = document.documentElement;
      const mode = root.getAttribute('data-theme') === 'light' ? 'dark' : 'light';
      root.setAttribute('data-theme', mode);
      try { localStorage.setItem(THEME_KEY, mode); } catch (e) {}
    });
  }

  // ─── Focus helpers ───────────────────────────────────────────
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Keeps Tab / Shift+Tab inside `container` while it's open.
  function trapFocus(container, e) {
    if (e.key !== 'Tab') return;
    const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter(el => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  // ─── Cart Drawer ─────────────────────────────────────────────
  let cartReturnFocus = null;

  function isCartOpen() {
    const drawer = document.getElementById('cart-drawer');
    return !!(drawer && drawer.classList.contains('is-open'));
  }

  // `returnFocusTo`: element to refocus on close (defaults to whatever has focus now).
  function openCart(returnFocusTo) {
    const drawer = document.getElementById('cart-drawer');
    const overlay = document.getElementById('cart-overlay');
    if (!drawer) return;
    cartReturnFocus = returnFocusTo instanceof Element ? returnFocusTo : document.activeElement;
    drawer.classList.add('is-open');
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.classList.add('is-visible');
    document.getElementById('cart-icon')?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    document.getElementById('cart-close')?.focus();
  }

  function closeCart() {
    const drawer = document.getElementById('cart-drawer');
    const overlay = document.getElementById('cart-overlay');
    if (!drawer || !isCartOpen()) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('inert', '');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.classList.remove('is-visible');
    document.getElementById('cart-icon')?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (cartReturnFocus && document.contains(cartReturnFocus)) cartReturnFocus.focus();
    cartReturnFocus = null;
  }

  function initCartDrawer() {
    const drawer = document.getElementById('cart-drawer');
    // Re-fetch on open in case the cart changed in another tab.
    document.getElementById('cart-icon')?.addEventListener('click', () => {
      openCart();
      refreshCart();
    });
    document.getElementById('cart-close')?.addEventListener('click', closeCart);
    document.getElementById('cart-overlay')?.addEventListener('click', closeCart);
    if (!drawer) return;
    drawer.addEventListener('keydown', (e) => trapFocus(drawer, e));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isCartOpen()) closeCart();
    });
  }

  // ─── Mobile Menu ─────────────────────────────────────────────
  function initMobileMenu() {
    const toggle = document.getElementById('mobile-menu-toggle');
    const nav = document.getElementById('mobile-nav');
    if (!toggle || !nav) return;

    function setOpen(open) {
      nav.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? t('closeMenu', 'Close menu') : t('openMenu', 'Open menu'));
    }

    toggle.addEventListener('click', () => setOpen(!nav.classList.contains('is-open')));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) {
        setOpen(false);
        toggle.focus();
      }
    });
  }

  // ─── Header Search ───────────────────────────────────────────
  function initHeaderSearch() {
    const wrapper = document.querySelector('.header-search');
    const toggleBtn = document.getElementById('search-toggle');
    const input = document.getElementById('header-search-input');
    if (!wrapper || !toggleBtn || !input) return;

    function openSearch() {
      wrapper.classList.add('is-open');
      toggleBtn.setAttribute('aria-expanded', 'true');
      window.setTimeout(() => input.focus(), 50);
    }

    function closeSearch() {
      wrapper.classList.remove('is-open');
      toggleBtn.setAttribute('aria-expanded', 'false');
    }

    toggleBtn.addEventListener('click', () => {
      if (wrapper.classList.contains('is-open')) {
        closeSearch();
      } else {
        openSearch();
      }
    });

    document.addEventListener('click', (e) => {
      if (!wrapper.contains(e.target)) closeSearch();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeSearch();
    });
  }

  // ─── Product Gallery ─────────────────────────────────────────
  function initGallery() {
    const main = document.getElementById('gallery-main-img');
    const thumbs = document.querySelectorAll('.product-gallery__thumb');
    if (!main || !thumbs.length) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const src = thumb.getAttribute('data-src');
        if (src) {
          main.srcset = thumb.getAttribute('data-srcset') || '';
          main.src = src;
          main.alt = thumb.querySelector('img')?.alt || main.alt;
          thumbs.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        }
      });
    });
  }

  // ─── Quantity Selector ───────────────────────────────────────
  function initQtySelectors() {
    document.querySelectorAll('.qty-selector').forEach(selector => {
      const input = selector.querySelector('input');
      const dec = selector.querySelector('[data-action="dec"]');
      const inc = selector.querySelector('[data-action="inc"]');
      if (!input) return;
      dec && dec.addEventListener('click', () => {
        const v = parseInt(input.value, 10) || 1;
        if (v > 1) input.value = v - 1;
      });
      inc && inc.addEventListener('click', () => {
        const v = parseInt(input.value, 10) || 1;
        input.value = v + 1;
      });
    });
  }

  // ─── Variant Selector ────────────────────────────────────────
  // Resolves the variant from the selected value of *every* option group,
  // then syncs the hidden id, price, buttons, gallery image and URL.
  function initVariants() {
    const form = document.getElementById('product-form');
    const dataEl = document.getElementById('product-variants');
    if (!form || !dataEl) return;

    const variants = JSON.parse(dataEl.textContent);
    const prices = JSON.parse(document.getElementById('product-variant-prices')?.textContent || '{}');
    const groups = Array.from(form.querySelectorAll('[data-variant-group]'));

    function selectedOptions() {
      return groups.map(group => {
        const active = group.querySelector('.variant-btn.is-active');
        return active ? active.dataset.optionValue : null;
      });
    }

    function update() {
      const options = selectedOptions();
      const variant = variants.find(v => v.options.every((value, i) => value === options[i]));

      const hiddenInput = document.getElementById('variant-id');
      if (hiddenInput) hiddenInput.value = variant ? variant.id : '';

      setProductFormState(form, variant);

      const priceEl = document.getElementById('product-price');
      const price = variant && prices[variant.id];
      if (priceEl && price) {
        priceEl.textContent = price.price;
        if (price.compare) {
          const compare = document.createElement('s');
          compare.textContent = price.compare;
          priceEl.append(compare);
        }
      }

      if (variant && variant.featured_image) {
        const thumb = document.querySelector(`.product-gallery__thumb[data-image-id="${variant.featured_image.id}"]`);
        if (thumb) thumb.click();
      }

      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    groups.forEach(group => {
      group.addEventListener('click', (e) => {
        const btn = e.target.closest('.variant-btn');
        if (!btn) return;
        group.querySelectorAll('.variant-btn').forEach(b => {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        update();
      });
    });
  }

  // Enables/disables the purchase buttons for the given variant
  // (undefined = that option combination doesn't exist).
  function setProductFormState(form, variant) {
    const addBtn = form.querySelector('[data-add-to-cart]');
    const buyBtn = form.querySelector('[data-buy-now]');
    const available = !!(variant && variant.available);
    if (addBtn) {
      addBtn.disabled = !available;
      addBtn.textContent = available
        ? t('addToCart', 'ADD TO CART')
        : (variant ? t('soldOut', 'SOLD OUT') : t('unavailable', 'UNAVAILABLE'));
    }
    if (buyBtn) buyBtn.hidden = !available;
    showFormError(form, '');
  }

  function showFormError(form, message) {
    const el = form.querySelector('#product-form-error');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  // ─── Add to Cart ─────────────────────────────────────────────
  function initAddToCart() {
    const form = document.getElementById('product-form');
    if (!form) return;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const buyNow = !!(e.submitter && e.submitter.hasAttribute('data-buy-now'));
      const btn = buyNow ? e.submitter : form.querySelector('[data-add-to-cart]');
      const label = btn.textContent;
      const variantId = form.querySelector('#variant-id')?.value;
      const qty = form.querySelector('[name="quantity"]')?.value || 1;
      if (!variantId) return;

      btn.disabled = true;
      btn.textContent = buyNow ? t('loading', 'LOADING...') : t('adding', 'ADDING...');
      showFormError(form, '');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: parseInt(qty, 10), sections: CART_SECTION })
        });
        if (res.ok && buyNow) {
          window.location.href = '/checkout';
          return;
        } else if (res.ok) {
          const data = await res.json();
          renderCartSections(data.sections);
          openCart(btn); // btn is disabled right now, so it has lost focus
        } else {
          const data = await res.json().catch(() => ({}));
          showFormError(form, data.description || t('addError', 'Could not add this item to your cart.'));
        }
      } catch (err) {
        console.error('Add to cart error:', err);
        showFormError(form, t('addError', 'Could not add this item to your cart.'));
      } finally {
        btn.disabled = false;
        btn.textContent = label;
      }
    });
  }

  // ─── Cart rendering ──────────────────────────────────────────
  // Cart markup lives only in Liquid. After the cart changes, Shopify's Section
  // Rendering API returns the affected sections freshly rendered and we swap
  // them in: the `cart-drawer` section, plus the /cart page's section (marked
  // data-cart-page="<section id>") when it's on the page.
  const CART_SECTION = 'cart-drawer';

  function cartPage() {
    return document.querySelector('[data-cart-page]');
  }

  function cartSectionIds() {
    const page = cartPage();
    return page ? [CART_SECTION, page.dataset.cartPage] : [CART_SECTION];
  }

  function parseSection(html) {
    return html ? new DOMParser().parseFromString(html, 'text/html') : null;
  }

  // `sections` is the { sectionId: html } map Shopify returns.
  function renderCartSections(sections) {
    if (!sections) return;

    // Drawer: swap only the parts that change, so its open state is kept.
    const drawer = parseSection(sections[CART_SECTION]);
    if (drawer) {
      const freshItems = drawer.getElementById('cart-items');
      const itemsEl = document.getElementById('cart-items');
      if (freshItems && itemsEl) {
        patchCartLines(itemsEl, freshItems);
        itemsEl.dataset.itemCount = freshItems.dataset.itemCount;
        renderCartCount(Number(freshItems.dataset.itemCount) || 0);
      }
      const freshSubtotal = drawer.getElementById('cart-subtotal');
      const subtotalEl = document.getElementById('cart-subtotal');
      if (freshSubtotal && subtotalEl) subtotalEl.textContent = freshSubtotal.textContent;
    }

    const page = cartPage();
    const freshPage = page && parseSection(sections[page.dataset.cartPage]);
    const freshPageEl = freshPage && freshPage.querySelector('[data-cart-page]');
    if (freshPageEl) {
      const list = page.querySelector('.cart-page__items');
      const freshList = freshPageEl.querySelector('.cart-page__items');
      if (list && freshList) {
        patchCartLines(list, freshList);
        const subtotal = page.querySelector('.cart-page__subtotal strong');
        const freshSubtotal = freshPageEl.querySelector('.cart-page__subtotal strong');
        if (subtotal && freshSubtotal) subtotal.textContent = freshSubtotal.textContent;
      } else {
        page.innerHTML = freshPageEl.innerHTML; // cart became empty (or was empty)
      }
    }
  }

  // Updates cart rows in place from freshly rendered markup. Rows that stay keep
  // their image element (only .cart-item__details is swapped), so thumbnails
  // don't reload and flicker on every quantity change.
  function patchCartLines(container, fresh) {
    const oldRows = new Map(Array.from(container.querySelectorAll('.cart-item'), row => [row.dataset.lineKey, row]));
    const freshRows = Array.from(fresh.querySelectorAll('.cart-item'));
    if (!oldRows.size || !freshRows.length) {
      container.innerHTML = fresh.innerHTML;
      return;
    }

    const rows = freshRows.map(freshRow => {
      const row = oldRows.get(freshRow.dataset.lineKey);
      if (!row) return document.importNode(freshRow, true);
      oldRows.delete(freshRow.dataset.lineKey);
      row.querySelector('.cart-item__details').replaceWith(document.importNode(freshRow.querySelector('.cart-item__details'), true));
      return row;
    });

    oldRows.forEach(row => row.remove());
    // Only move rows that are out of place, so unchanged rows stay put.
    let prev = null;
    rows.forEach(row => {
      const expected = prev ? prev.nextElementSibling : container.firstElementChild;
      if (row !== expected) container.insertBefore(row, expected);
      prev = row;
    });
  }

  function renderCartCount(count) {
    const badge = document.getElementById('cart-count');
    if (!badge) return;
    badge.textContent = count;
    badge.hidden = count === 0;
  }

  async function refreshCart() {
    try {
      const res = await fetch(`/?sections=${CART_SECTION}`);
      renderCartSections(await res.json());
    } catch (err) {
      console.error(err);
    }
  }

  async function changeLineQuantity(lineKey, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity, sections: cartSectionIds() })
      });
      if (!res.ok) {
        console.error('Cart change failed:', res.status);
        return false;
      }
      renderCartSections((await res.json()).sections);
      return true;
    } catch (err) {
      console.error('Cart change error:', err);
      return false;
    }
  }

  // Wires +/- and remove buttons inside `container` using event delegation,
  // so it keeps working after the contents are re-rendered.
  const LINE_ACTIONS = ['data-remove-item', 'data-qty-increase', 'data-qty-decrease'];

  function initCartItemControls(container) {
    if (!container) return;

    container.addEventListener('click', async (e) => {
      const row = e.target.closest('.cart-item');
      const action = LINE_ACTIONS.find(attr => e.target.closest(`[${attr}]`));
      if (!row || !row.dataset.lineKey || !action) return;

      const lineKey = row.dataset.lineKey;
      const current = parseInt(row.querySelector('.cart-item__qty-value').textContent, 10);
      const quantity = action === 'data-remove-item' ? 0
        : action === 'data-qty-increase' ? current + 1
        : Math.max(0, current - 1);

      if (!(await changeLineQuantity(lineKey, quantity))) return;

      // The re-render replaced the clicked button: refocus its replacement
      // (or the first control left, if the line was removed).
      const same = container.querySelector(`.cart-item[data-line-key="${CSS.escape(lineKey)}"] [${action}]`);
      (same || container.querySelector(FOCUSABLE))?.focus();
    });
  }

  // ─── Init ─────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', () => {
    initThemeToggle();

    // Cart
    initCartDrawer();
    initCartItemControls(document.getElementById('cart-items'));
    initCartItemControls(cartPage());

    // Features
    initMobileMenu();
    initHeaderSearch();
    initGallery();
    initQtySelectors();
    initVariants();
    initAddToCart();
  });
})();
